import { useState, useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { Purchases, CustomerInfo, PurchasesOffering } from "@revenuecat/purchases-capacitor";
import { supabase } from "@/integrations/supabase/client";

export const useRevenueCat = () => {
  const [isConfigured, setIsConfigured] = useState(false);
  const [offerings, setOfferings] = useState<PurchasesOffering | null>(null);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [initializationError, setInitializationError] = useState<string | null>(null);
  const initCalledRef = useRef(false);

  useEffect(() => {
    // Only run on iOS and only once
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios" && !initCalledRef.current) {
      initCalledRef.current = true;
      initializeRevenueCat();
    }
  }, []);

  const initializeRevenueCat = async () => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "ios") {
      return;
    }

    // Prevent concurrent initialization
    if (isInitializing) return;

    try {
      setIsInitializing(true);
      setInitializationError(null);

      const envApiKey = import.meta.env.VITE_REVENUECAT_PUBLIC_KEY as string | undefined;

      // Prefer env key if it's already correct
      let apiKey = envApiKey?.trim();

      // If the app bundle was built with a wrong key (e.g. sk_), fetch the correct public SDK key at runtime
      if (!apiKey || apiKey.startsWith("sk_")) {
        try {
          const { data, error } = await supabase.functions.invoke("revenuecat-public-key");
          if (error) throw error;
          apiKey = (data?.apiKey as string | undefined)?.trim();
        } catch (e) {
          // fall through to validation below
          console.warn("[RevenueCat] Failed to fetch SDK key at runtime:", e);
        }
      }

      // Validate API key is present and is a public key (not a secret key)
      if (!apiKey) {
        throw new Error("RevenueCat API key not configured");
      }

      if (apiKey.startsWith("sk_")) {
        throw new Error("Invalid API key type - use public SDK key, not secret key");
      }

      console.log("[RevenueCat] Configuring with API key prefix:", apiKey.substring(0, 10) + "...");

      // Configure RevenueCat with your public API key
      await Purchases.configure({ apiKey });

      console.log("[RevenueCat] SDK configured successfully");

      // Log in with Supabase user ID so backend can verify subscription
      // This must happen BEFORE any purchase attempts
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session?.user?.id) {
        try {
          const loginResult = await Purchases.logIn({ appUserID: session.user.id });
          console.log("[RevenueCat] Logged in with Supabase user ID:", session.user.id);
          if (loginResult.customerInfo) {
            setCustomerInfo(loginResult.customerInfo);
          }
        } catch (loginError: any) {
          // If login fails with "already logged in", that's okay - continue
          console.warn("[RevenueCat] Login warning:", loginError?.message || loginError);
        }
      } else {
        console.log("[RevenueCat] No active session - will configure as anonymous user");
      }

      setIsConfigured(true);

      // Load offerings
      const offeringsResult = await Purchases.getOfferings();
      console.log("[RevenueCat] Offerings response:", JSON.stringify(offeringsResult, null, 2));

      if (offeringsResult.current) {
        setOfferings(offeringsResult.current);
        console.log(
          "[RevenueCat] Available packages:",
          offeringsResult.current.availablePackages.map((pkg) => ({
            identifier: pkg.identifier,
            packageType: pkg.packageType,
            product: pkg.product?.identifier,
          })),
        );
      } else {
        console.warn("[RevenueCat] No current offering found - check RevenueCat dashboard configuration");
        setInitializationError("No subscription plans available. Please check configuration.");
      }

      // Get customer info (if not already set from login)
      if (!customerInfo) {
        const { customerInfo: info } = await Purchases.getCustomerInfo();
        setCustomerInfo(info);
        console.log("[RevenueCat] Customer info loaded:", {
          originalAppUserId: info.originalAppUserId,
          activeEntitlements: Object.keys(info.entitlements.active),
        });
      }

      console.log("[RevenueCat] Initialized successfully");
    } catch (error: any) {
      console.error("[RevenueCat] Initialization error:", error);
      console.error("[RevenueCat] Error details:", JSON.stringify(error, Object.getOwnPropertyNames(error), 2));
      setIsConfigured(false);
      setInitializationError(error?.message ?? "Failed to initialize purchases.");
    } finally {
      setIsInitializing(false);
    }
  };

  const purchaseSubscription = async (packageIdentifier: string): Promise<boolean> => {
    try {
      if (!offerings) {
        throw new Error("No offerings available");
      }

      // Log available packages for debugging
      console.log("[RevenueCat] Looking for package:", packageIdentifier);
      console.log(
        "[RevenueCat] Available packages:",
        offerings.availablePackages.map((pkg) => ({
          identifier: pkg.identifier,
        })),
      );

      const packageToPurchase = offerings.availablePackages.find((pkg) => pkg.identifier === packageIdentifier);

      if (!packageToPurchase) {
        const availableIds = offerings.availablePackages.map((p) => p.identifier).join(", ");
        throw new Error(`Package "${packageIdentifier}" not found. Available packages: ${availableIds}`);
      }

      const purchaseResult = await Purchases.purchasePackage({
        aPackage: packageToPurchase,
      });

      console.log("[RevenueCat] Purchase successful:", purchaseResult);

      // Update customer info after purchase
      const { customerInfo } = await Purchases.getCustomerInfo();
      setCustomerInfo(customerInfo);

      return true;
    } catch (error: any) {
      // RevenueCat errors can be complex objects, extract all possible error info
      let errorMessage = "Purchase failed";
      let errorCode = null;
      let underlyingError = null;

      // Try to extract error message from various possible locations
      if (typeof error === "string") {
        errorMessage = error;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.underlyingErrorMessage) {
        errorMessage = error.underlyingErrorMessage;
      } else if (error?.readableErrorCode) {
        errorMessage = `Error: ${error.readableErrorCode}`;
      } else if (error?.code) {
        errorCode = error.code;
        errorMessage = `Purchase error (code: ${error.code})`;
      }

      // Try to get underlying error
      if (error?.underlyingErrorMessage) {
        underlyingError = error.underlyingErrorMessage;
      } else if (error?.underlyingError?.localizedDescription) {
        underlyingError = error.underlyingError.localizedDescription;
      } else if (error?.underlyingError?.message) {
        underlyingError = error.underlyingError.message;
      }

      // Log comprehensive error details
      console.error("[RevenueCat] Purchase error:", error);
      console.error("[RevenueCat] Error details:", {
        message: error?.message,
        code: error?.code,
        underlyingErrorMessage: error?.underlyingErrorMessage,
        underlyingError: error?.underlyingError,
        userCancelled: error?.userCancelled,
        readableErrorCode: error?.readableErrorCode,
        errorType: error?.constructor?.name,
        errorKeys: error ? Object.keys(error) : [],
      });

      // Try to stringify error for more details
      try {
        const errorString = JSON.stringify(error, Object.getOwnPropertyNames(error), 2);
        console.error("[RevenueCat] Full error JSON:", errorString);
      } catch (e) {
        console.error("[RevenueCat] Could not stringify error:", e);
      }

      // User cancelled - not an error, but log for debugging
      if (error?.userCancelled === true) {
        console.log("[RevenueCat] User explicitly cancelled the purchase");
        return false;
      }

      // Check for specific error codes that indicate authentication issues
      const isAuthError = 
        errorMessage?.includes("Finance Authentication") ||
        errorMessage?.includes("authentication") ||
        underlyingError?.includes("Finance Authentication") ||
        error?.code === 2 ||
        error?.readableErrorCode === "STORE_PROBLEM";

      if (isAuthError) {
        console.error("[RevenueCat] Authentication/Store problem detected - user may need sandbox tester account");
      }

      // Use underlying error if available, otherwise use extracted message
      const finalMessage = underlyingError || errorMessage;

      // Re-throw with more context
      const enhancedError = new Error(finalMessage);
      (enhancedError as any).originalError = error;
      (enhancedError as any).errorCode = errorCode || error?.code;
      (enhancedError as any).isAuthError = isAuthError;
      throw enhancedError;
    }
  };

  const restorePurchases = async (): Promise<CustomerInfo> => {
    try {
      const { customerInfo } = await Purchases.restorePurchases();
      setCustomerInfo(customerInfo);
      console.log("[RevenueCat] Purchases restored");
      return customerInfo;
    } catch (error) {
      console.error("[RevenueCat] Restore error:", error);
      throw error;
    }
  };

  const checkSubscriptionStatus = (): boolean => {
    if (!customerInfo) return false;

    // Check if user has the "Global Chat Connect Pro" entitlement
    return customerInfo.entitlements.active["Global Chat Connect Pro"] !== undefined;
  };

  const hasActiveEntitlement = (entitlementId: string): boolean => {
    if (!customerInfo) return false;
    return customerInfo.entitlements.active[entitlementId] !== undefined;
  };

  const isIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";

  return {
    isIOS,
    isConfigured,
    isInitializing,
    initializationError,
    offerings,
    customerInfo,
    purchaseSubscription,
    restorePurchases,
    checkSubscriptionStatus,
    hasActiveEntitlement,
    reinitialize: initializeRevenueCat,
  };
};
